import { create, StateCreator, StoreApi, UseBoundStore } from 'zustand';
import { persist, PersistOptions } from 'zustand/middleware';
import { createMenuSlice, MenuState } from './slices/menuSlice';
import { createTabSlice, TabState } from './slices/tabSlice';
import { createGraphSlice, GraphState } from './slices/chartSlice';
import { createNotificationSlice, NotificationState } from './slices/notificationSlice';
import { createConnectivityViewSlice } from './slices/connectivityChartSlice';
import { ConnectivityViewSlice } from './slices/connectivityChartSlice';
import { createModalSlice, ModalSlice } from './slices/modalSlice';
import { createTaskTrackerSlice, TaskTrackerSlice } from './slices/taskTrasckerSlice';
import { PageState, createPageSlice } from '@/app/store/slices/pageSlice';
import { createLayoutSlice, LayoutState } from '@/app/store/slices/layoutSLice';
import { CUSTOMIZED_FILTER_KEY } from '@/legacy/constants';

export const LOCAL_STORAGE_KEY = 'store';

type StoreState = PageState &
	MenuState &
	TabState &
	GraphState &
	NotificationState &
	ConnectivityViewSlice &
	ModalSlice &
	TaskTrackerSlice &
	LayoutState;

type MyPersist = (
	config: StateCreator<StoreState>,
	options: PersistOptions<Partial<StoreState>, Partial<StoreState>>,
) => StateCreator<StoreState>;

export const useStore = create<StoreState>(
	(persist as MyPersist)(
		(set, get, store) => {
			const graphSlice = createGraphSlice(set, get, store);
			return {
				...createPageSlice(set, graphSlice.setSelectedDateForGraph),
				...createMenuSlice(set),
				...createTabSlice(set, get, store),
				...createNotificationSlice(set, get, store),
				...createConnectivityViewSlice(set),
				...createModalSlice(set),
				...createTaskTrackerSlice(set),
				...createLayoutSlice(set),
				...graphSlice,
			};
		},
		{
			name: LOCAL_STORAGE_KEY,
			partialize: (state) => {
				const { sessionFilters, filtersDisabledByKey, ...rest } = state;
				return rest;
			},
			version: 2,
			migrate: (persistedState, version) => {
				if (version < 2) {
					const {
						useErp: _e,
						useAlerts: _a,
						useConciliations: _c,
						...rest
					} = persistedState as Record<string, unknown>;
					return rest as Partial<StoreState>;
				}
				return persistedState as Partial<StoreState>;
			},
			onRehydrateStorage: () => (hydratedState) => {
				// Migrate any stored filter that still contains computed date fields
				// (old snake_case or camelCase timestamps) to the new label-only shape.
				if (!hydratedState?.filters) return;
				const updatedFilters = { ...hydratedState.filters };
				let hasChanges = false;
				for (const [pageKey, pageFilter] of Object.entries(hydratedState.filters)) {
					const date = pageFilter.date as Record<string, unknown> | undefined;
					if (!date) continue;
					const label = date['label'] as string | undefined;
					if (!label || label === CUSTOMIZED_FILTER_KEY) continue;
					const hasExtraFields = Object.keys(date).some((k) => k !== 'label');
					if (hasExtraFields) {
						updatedFilters[pageKey] = { ...pageFilter, date: { label } };
						hasChanges = true;
					}
				}
				if (hasChanges) {
					useStore.setState({ filters: updatedFilters });
				}
			},
		},
	),
);

export const clearZustandStore = () => {
	const state = useStore.getState();
	state.resetPageSlice?.();
	state.resetMenuSlice?.();
	state.resetTabSlice?.();
	state.resetNotificationSlice?.();
	state.resetConnectivityViewSlice?.();
	state.resetModalSlice?.();
	state.resetTaskTrackerSlice?.();

	(
		useStore as UseBoundStore<StoreApi<StoreState>> & {
			persist: { clearStorage: () => void };
		}
	).persist.clearStorage();
};

export const zustandStore = useStore;
